import { Col, Form, Input, Row } from 'antd';
import type { NamePath } from 'antd/lib/form/interface';
import classnames from 'classnames';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { passwordRules, validatePasswordConditionSeparatly } from 'common/validators';
import { CheckCircleLineIcon, CloseCircleLineIcon, Eye2LineIcon, EyeCloseIcon } from '../../icons';
import { Text } from '../../typography';
import { Item } from '../item';

type InputP = React.ComponentProps<typeof Input.Password>;

type ItemP = {
  label?: string;
} & Omit<React.ComponentProps<typeof Form.Item>, 'children' | 'label'>;

const ValidationInfo = ({
  showErrorState,
  title,
  valid,
}: {
  showErrorState: boolean;
  title: string;
  valid: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <li
      className={classnames({
        // TODO this is too jumping in UI
        'password-input__validated': showErrorState || valid,
      })}
    >
      <Text
        aria-live="polite"
        className="password-input__text"
        // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
        type={showErrorState || valid ? (valid ? 'success' : 'danger') : undefined}
      >
        {showErrorState || valid ? (
          // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
          valid ? (
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- Fix this
            <CheckCircleLineIcon aria-label="requirement completed" role="img" />
          ) : (
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- Fix this
            <CloseCircleLineIcon aria-label="password requires" role="img" />
          )
        ) : null}
        {t(title)}
      </Text>
    </li>
  );
};

const PasswordValidationInfo = ({ idPrefix = 'passwordValidation', name }: { idPrefix?: string; name?: NamePath }) => {
  const { t } = useTranslation();

  if (!name) {
    return null;
  }

  return (
    // eslint-disable-next-line no-restricted-syntax -- We cannot find type that would fit here
    <Form.Item shouldUpdate>
      {({ getFieldError, getFieldValue }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const password: string | undefined = getFieldValue(name);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const hasValidationErrors = Boolean(password && getFieldError(name)?.length > 0);
        const passwordCheck = validatePasswordConditionSeparatly(password ?? '');

        return (
          <Row aria-atomic className="password-input__validation" gutter={[0, 'ms']} id={`${idPrefix}_password_help`}>
            <Col span={24}>
              <Text>{t('The password must contain following: ')}</Text>
            </Col>
            <Col span={24}>
              <ul className="password-input__validation__grid-container">
                {passwordRules.map((rule) => (
                  <ValidationInfo
                    key={rule.key}
                    showErrorState={hasValidationErrors}
                    title={rule.title}
                    valid={passwordCheck[rule.key]}
                  />
                ))}
              </ul>
            </Col>
            <Text aria-live="polite" className="visually-hidden">
              {`${passwordCheck.numberOfFullfilledConditions} of ${passwordRules.length} requirements completed`}
            </Text>
          </Row>
        );
      }}
    </Form.Item>
  );
};

type P = {
  input?: InputP;
  item: ItemP;
  showValidation?: boolean;
};

const PasswordInput = ({ input, item, showValidation }: P) => (
  <>
    <Item {...item}>
      <Input.Password
        {...input}
        aria-labelledby="passwordValidation_password_help"
        iconRender={(visible) => (visible ? <Eye2LineIcon cursor="pointer" /> : <EyeCloseIcon cursor="pointer" />)}
        size="large"
      />
    </Item>

    {showValidation ? <PasswordValidationInfo name={item.name} /> : null}
  </>
);

export default PasswordInput;
